import { useState } from "react";
import Cropper from "react-easy-crop";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";

const PRESETS = [
  { label: "Free", value: null },
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
];

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.src = url;
  });

async function canvasToBlob(imageSrc, areaPct) {
  const image = await createImage(imageSrc);
  const naturalW = image.naturalWidth;
  const naturalH = image.naturalHeight;

  // Crop area comes as percentages of the natural image size — immune to
  // container-measurement bugs. Fall back to the full image if degenerate.
  const area =
    areaPct && areaPct.width > 1 && areaPct.height > 1
      ? areaPct
      : { x: 0, y: 0, width: 100, height: 100 };

  const sx = Math.max(0, Math.round((area.x / 100) * naturalW));
  const sy = Math.max(0, Math.round((area.y / 100) * naturalH));
  const sw = Math.max(1, Math.round((area.width / 100) * naturalW));
  const sh = Math.max(1, Math.round((area.height / 100) * naturalH));

  const canvas = document.createElement("canvas");
  const maxDim = 1800;
  const scale = Math.min(1, maxDim / Math.max(sw, sh));
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

export default function CropDialog({ cropFile, uploading, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(null);
  const [areaPct, setAreaPct] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  if (!cropFile) return null;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const blob = await canvasToBlob(cropFile.src, areaPct);
      await onConfirm(blob, caption.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && !uploading && onCancel()}>
      <DialogContent className="max-w-lg border-white/10 bg-den" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold uppercase tracking-tight">
            Crop the fluff
          </DialogTitle>
        </DialogHeader>

        <div className="relative h-[46vh] overflow-hidden rounded-xl bg-void" data-testid="crop-area">
          <Cropper
            image={cropFile.src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(areaPct) => setAreaPct(areaPct)}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              data-testid={`crop-preset-${p.label.replace(":", "")}`}
              onClick={() => setAspect(p.value)}
              className={`rounded-full border px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wide transition-colors ${
                aspect === p.value
                  ? "border-husko-cyan bg-husko-cyan/10 text-husko-cyan"
                  : "border-white/15 text-slate-400 hover:text-slate-200"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="w-10 shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500">Zoom</span>
          <Slider
            value={[zoom]}
            onValueChange={(v) => setZoom(v[0])}
            min={1}
            max={3}
            step={0.05}
            data-testid="crop-zoom-slider"
          />
        </div>

        <Input
          data-testid="crop-caption-input"
          placeholder="Caption (optional) — e.g. 'Maximum fluff'"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={120}
          className="border-white/15 bg-void"
        />

        <div className="flex justify-end gap-3">
          <Button
            data-testid="crop-cancel-button"
            variant="ghost"
            onClick={onCancel}
            disabled={busy || uploading}
            className="cursor-pointer rounded-full"
          >
            Cancel
          </Button>
          <Button
            data-testid="crop-confirm-button"
            onClick={handleConfirm}
            disabled={busy || uploading}
            className="cursor-pointer gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-magenta font-display font-extrabold uppercase text-void hover:opacity-90"
          >
            {busy || uploading ? <Loader2 size={16} className="animate-spin" /> : null}
            Add to gallery
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
